import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { stopSTT } from './sttClient';
import { LanguageProvider } from './contexts/LanguageContext';
import Home from './components/Home';
import ExploreLearning from './components/ExploreLearning';
import Learn from './components/Learn';
import SpeakingPage from './components/SpeakingPage';
import ReadingPage from './components/ReadingPage';
import WritingPage from './components/WritingPage';
import ListeningPage from './components/ListeningPage';
import BusinessScenario from './components/BusinessScenario';
import CulturalInsights from './components/CulturalInsights';
import CulturalInsightsChat from './components/CulturalInsightsChat';
import CulturalInsightsChallenge from './components/CulturalInsightsChallenge';
import CulturalInsightsChallengeQuiz from './components/CulturalInsightsChallengeQuiz';
import CIChatHistory from './components/CIChatHistory';
import BusinessConversation from './components/BusinessConversation';
import ConversationSummary from './components/ConversationSummary';
import LogoutPage from './components/LogoutPage';
import ChatHistory from './components/ChatHistory';
import AdminReport from './components/AdminReport';
import SessionTimeoutModal from './components/SessionTimeoutModal';
import useSessionTimeout from './hooks/useSessionTimeout';
import './App.css';

function AppContent() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionModalType, setSessionModalType] = useState(null); // 'warning' or 'expired'
  
  // Check if running locally
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  // Session timeout handlers
  const handleSessionWarning = () => {
    setSessionModalType('warning');
    // Stop any active microphone recording
    stopSTT();
    // Pause activity tracking when warning modal appears
    pauseTimeout();
    
    // Set a 2-minute timer for auto-expiration
    setTimeout(() => {
      // Only expire if still showing warning (user didn't click Continue)
      setSessionModalType(current => {
        if (current === 'warning') {
          handleSessionExpired();
          return 'expired';
        }
        return current;
      });
    }, 1 * 60 * 1000); // 1 minute
  };

  const handleSessionExpired = () => {
    setSessionModalType('expired');
    // Stop any active microphone recording
    stopSTT();
    // Clear user data
    setUser(null);
    // Call logout API
    if (!isLocal) {
      axios.post('/auth/logout', {}, { withCredentials: true }).catch(err => {
        console.error('Logout error:', err);
      });
    }
  };

  const handleContinueSession = () => {
    setSessionModalType(null);
    // Resume activity tracking and reset timers
    resumeTimeout();
  };

  const handleLogout = () => {
    setSessionModalType(null);
    setUser(null);
    if (!isLocal) {
      axios.post('/auth/logout', {}, { withCredentials: true }).catch(err => {
        console.error('Logout error:', err);
      });
    }
    navigate('/logout');
  };

  const handleLoginAgain = () => {
    setSessionModalType(null);
    navigate('/logout');
  };

  // Initialize session timeout (only for production)
  const { resetTimers, pauseTimeout, resumeTimeout } = useSessionTimeout(
    !isLocal ? handleSessionWarning : null,
    !isLocal ? handleSessionExpired : null
  );

  // Axios interceptor to detect session expiry from API errors
  useEffect(() => {
    if (isLocal) return;
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        const url = error.config?.url || '';
        // Skip session expired for logout page, debug/token, disclaimer, and auth endpoints
        if (url.includes('/debug/token') || url.includes('/disclaimer/') || url.includes('/auth/') || url.includes('/ci-challenge/') || window.location.pathname === '/logout') {
          return Promise.reject(error);
        }
        const msg = String(error.response?.data?.error || error.response?.data?.message || '');
        if (error.response?.status === 401 || msg.includes('not authenticated') || msg.includes('Session unavailable')) {
          // Stop any active microphone recording before redirecting
          stopSTT();
          // Redirect to login
          window.location.href = "/auth/login?returnTo=" + encodeURIComponent(window.location.pathname);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [isLocal]);

  useEffect(() => {
    if (isLocal) {
      // Local development: Auto-login with dummy user
      setUser({ 
        name: 'Local Dev User', 
        email: 'dev@localhost.com', 
        id: 'local-dev-user' 
      });
      setLoading(false);
      // Fetch and store GenPal token in sessionStorage for testing
      const tokenUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api') + '/debug/token';
      axios.get(tokenUrl, { withCredentials: true })
        .then(tokenRes => {
          if (tokenRes.data?.token) {
            sessionStorage.setItem('genpal_token', tokenRes.data.token);
            sessionStorage.setItem('genpal_token_generated_at', tokenRes.data.generated_at);
          }
        })
        .catch(() => {});
    } else {
      // Production (Ubuntu): Check SSO authentication
      const checkAuth = (retryCount = 0) => {
        axios.get("/auth/me", { withCredentials: true })
          .then(res => {
            setUser(res.data);
            setLoading(false);
            // Fetch and store GenPal token in sessionStorage for security testing (non-prod only)
            axios.get("/lb-api/debug/token", { withCredentials: true })
              .then(tokenRes => {
                if (tokenRes.data?.token) {
                  sessionStorage.setItem('genpal_token', tokenRes.data.token);
                  sessionStorage.setItem('genpal_token_generated_at', tokenRes.data.generated_at);
                }
              })
              .catch(() => {}); // Silently fail in production
          })
          .catch(() => {
            // Retry once after a short delay (handles race condition after OAuth redirect)
            if (retryCount < 1) {
              setTimeout(() => checkAuth(retryCount + 1), 1000);
            } else {
              // Not authenticated, redirect to SSO login with return URL
              window.location.href = "/auth/login?returnTo=" + encodeURIComponent(window.location.pathname + window.location.search);
            }
          });
      };
      checkAuth();
    }
  }, [isLocal]);

  if (loading) return <div>Loading...</div>;

  return (
    <LanguageProvider>
      <Routes>
        <Route path="/" element={<Home user={user} />} />
        <Route path="/explore-learning" element={<ExploreLearning user={user} />} />
        <Route path="/learn" element={<Learn user={user} />} />
        <Route path="/practice-speaking" element={<SpeakingPage user={user} />} />
        <Route path="/practice-reading" element={<ReadingPage user={user} />} />
        <Route path="/practice-writing" element={<WritingPage user={user} />} />
        <Route path="/practice-listening" element={<ListeningPage user={user} />} />
        <Route path="/practice-business" element={<BusinessScenario user={user} />} />
        <Route path="/cultural-insights" element={<CulturalInsights user={user} />} />
        <Route path="/cultural-insights-chat" element={<CulturalInsightsChat user={user} />} />
        <Route path="/cultural-insights-challenge" element={<CulturalInsightsChallenge user={user} />} />
        <Route path="/cultural-insights-challenge-quiz" element={<CulturalInsightsChallengeQuiz user={user} />} />
        <Route path="/ci-chat-history" element={<CIChatHistory user={user} />} />
        <Route path="/business-conversation" element={<BusinessConversation user={user} />} />
        <Route path="/conversation-summary" element={<ConversationSummary user={user} />} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route path="/chat-history" element={<ChatHistory user={user} />} />
        <Route path="/admin-report" element={<AdminReport user={user} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Session Timeout Modal */}
      {sessionModalType && (
        <SessionTimeoutModal
          type={sessionModalType}
          onContinue={handleContinueSession}
          onLogout={handleLogout}
          onLogin={handleLoginAgain}
        />
      )}
    </LanguageProvider>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
